import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { getApiHostname } from "../config/getApiHostname";

const hostname = getApiHostname();

axios.defaults.baseURL = hostname;
axios.defaults.headers.common["Content-Type"] = "application/json";
axios.defaults.headers.common.accept = "application/json";

const instance: AxiosInstance = axios.create({
  baseURL: hostname,
});

// Module-level re-auth handler set by React code via setOnUnauthorized().
// Invoked on any 401 response — the handler should trigger re-login (e.g.
// show a modal or redirect to the IdP). We coalesce concurrent 401s so only
// a single re-auth is kicked off even when multiple inflight polls fail.
let onUnauthorized: (() => Promise<void>) | null = null;
let inflightReauth: Promise<void> | null = null;

export const setOnUnauthorized = (
  handler: (() => Promise<void>) | null
): void => {
  onUnauthorized = handler;
};

// axios's settle() RESOLVES any response whose status is 0: `!response.status`
// short-circuits validateStatus, so a dropped connection, a CORS-blocked reply or
// a browser-cancelled request arrives at the caller as a *success* carrying an
// empty body. Callers then read that body as a payload - the observed failure was
// `columnar.x` on an undefined chart body, surfacing in Sentry as an app
// TypeError that no status-based filter can suppress, and on the S3 upload path
// it would report a file that never landed as uploaded. Reject instead, so React
// Query's retry/onError paths and every caller see it for what it is.
const rejectStatusZero = (response: AxiosResponse): AxiosResponse => {
  if (!response.status) {
    throw new AxiosError(
      "Network Error",
      AxiosError.ERR_NETWORK,
      response.config,
      response.request,
      response
    );
  }
  return response;
};

// Registered on the bare `axios` too (as this module already does for
// `axios.defaults`) so the direct-to-S3 presigned PUTs in putSiteFile /
// putHhDataTemp, which deliberately bypass this instance, are covered as well.
axios.interceptors.response.use(rejectStatusZero);

instance.interceptors.response.use(rejectStatusZero, (error: AxiosError) => {
  if (error.response?.status === 401 && onUnauthorized) {
    if (!inflightReauth) {
      inflightReauth = (async () => {
        try {
          await onUnauthorized!();
        } finally {
          inflightReauth = null;
        }
      })();
    }
    // We don't await the relogin here: the individual request should still
    // reject so React Query's onError / retry logic fires. The next poll
    // interval or retry will pick up the fresh token from getAuthTokenBlocking.
  }
  return Promise.reject(error);
});

export default instance;
